/**
 * Mao AI Assistant Microcopy
 *
 * Consistent text generation for the assistant persona.
 * All text is in Romanian to match the app's primary language.
 */

import { getAssistantName } from './config';
import type { MaoIntent, MaoWorkingAction, MaoMessageType } from './types';

/**
 * Intent to Romanian verb mapping for button labels.
 */
const INTENT_VERBS: Record<MaoIntent, string> = {
  process: 'procesează',
  fix: 'repară',
  generate: 'generează',
  summarize: 'rezumă',
  optimize: 'optimizează',
  analyze: 'analizează',
  improve: 'îmbunătățește',
  help: 'ajută-mă',
};

/**
 * Intent to noun mapping for context.
 */
const INTENT_NOUNS: Record<MaoIntent, string> = {
  process: 'lista',
  fix: 'asta',
  generate: 'textul',
  summarize: 'conținutul',
  optimize: 'textul',
  analyze: 'datele',
  improve: 'mesajul',
  help: 'să înțeleg',
};

/**
 * Working action to Romanian text mapping.
 */
const WORKING_ACTIONS: Record<MaoWorkingAction, string> = {
  processing: 'procesează',
  analyzing: 'analizează',
  generating: 'generează',
  fixing: 'repară',
  optimizing: 'optimizează',
  checking: 'verifică',
  thinking: 'gândește',
};

/**
 * Generates a button label for the given intent.
 *
 * @example
 * maoLabel('process') // "Mao, procesează lista!"
 * maoLabel('fix') // "Mao, repară asta!"
 */
export function maoLabel(intent: MaoIntent, customNoun?: string): string {
  const name = getAssistantName();
  const verb = INTENT_VERBS[intent];
  const noun = customNoun || INTENT_NOUNS[intent];
  return `${name}, ${verb} ${noun}!`;
}

/**
 * Generates a short button label (just verb).
 *
 * @example
 * maoLabelShort('process') // "Mao, procesează!"
 */
export function maoLabelShort(intent: MaoIntent): string {
  const name = getAssistantName();
  const verb = INTENT_VERBS[intent];
  return `${name}, ${verb}!`;
}

/**
 * Generates working state text.
 *
 * @example
 * maoWorkingText('processing') // "Mao procesează..."
 * maoWorkingText('analyzing') // "Mao analizează..."
 */
export function maoWorkingText(action: MaoWorkingAction = 'processing'): string {
  const name = getAssistantName();
  const actionText = WORKING_ACTIONS[action];
  return `${name} ${actionText}...`;
}

/**
 * Generates a simple working text with the assistant name.
 *
 * @example
 * maoWorkingSimple() // "Mao lucrează..."
 */
export function maoWorkingSimple(): string {
  const name = getAssistantName();
  return `${name} lucrează...`;
}

/**
 * Success messages based on context.
 */
const SUCCESS_MESSAGES = {
  default: 'Gata. Am pregătit tot ce am putut.',
  processed: 'Am procesat totul cu succes.',
  generated: 'Am generat conținutul pentru tine.',
  fixed: 'Am reparat ce trebuia.',
  optimized: 'Am optimizat textul.',
  analyzed: 'Am terminat analiza.',
};

/**
 * Generates success text.
 *
 * @example
 * maoSuccessText() // "Gata. Am pregătit tot ce am putut."
 * maoSuccessText('processed') // "Am procesat totul cu succes."
 */
export function maoSuccessText(
  context: keyof typeof SUCCESS_MESSAGES = 'default',
): string {
  return SUCCESS_MESSAGES[context];
}

/**
 * Generates text for items that need review.
 */
export function maoReviewText(count?: number): string {
  if (count !== undefined && count > 0) {
    return `Am marcat ${count} ${count === 1 ? 'element' : 'elemente'} pentru verificare.`;
  }
  return 'Am marcat câteva lucruri pentru tine.';
}

/**
 * Generates error text with optional retry prompt.
 *
 * @example
 * maoErrorText(true) // "Nu a mers din prima. Vrei să încerc iar?"
 * maoErrorText(false) // "A apărut o problemă."
 */
export function maoErrorText(canRetry: boolean = true): string {
  if (canRetry) {
    return 'Nu a mers din prima. Vrei să încerc iar?';
  }
  return 'A apărut o problemă.';
}

/**
 * Generates error text with specific error context.
 */
export function maoErrorWithContext(errorMessage: string): string {
  return `Hmm, ceva nu a mers: ${errorMessage}`;
}

/**
 * Message type to default text mapping.
 */
export function maoMessageDefault(type: MaoMessageType): string {
  switch (type) {
    case 'info':
      return 'Iată ce am găsit.';
    case 'success':
      return maoSuccessText();
    case 'warning':
      return 'Atenție la câteva detalii.';
    case 'error':
      return maoErrorText(true);
    case 'review':
      return maoReviewText();
  }
}

/**
 * Get the assistant name for use in components.
 * Re-exported for convenience.
 */
export { getAssistantName };
